import React, { useState, useEffect, useRef } from 'react';

export default function AIChefAssistant({
  t,
  lang,
  onOpenRecipeModal,
  isFullscreen: controlledFs,
  setIsFullscreen: setControlledFs
}) {
  const [internalFs, setInternalFs] = useState(false);
  const isFs = controlledFs !== undefined ? controlledFs : internalFs;
  const setFs = setControlledFs || setInternalFs;

  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'bot',
      text: t.simWelcomeText || '',
      buttons: t.simWelcomeButtons || [],
      time: '10:00 AM'
    }
  ]);

  const [inputVal, setInputVal] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [clickedActions, setClickedActions] = useState(new Set());
  const [userProfile, setUserProfile] = useState({
    language: lang,
    persona: 'Mom / Homemaker',
    state: 'Tamil Nadu',
    diet: 'Vegetarian',
    headcount: 4,
    pantry: ['rice', 'toor dal', 'potato', 'tomato', 'onion', 'curd', 'curry leaves'],
    seenRecipes: []
  });

  const streamRef = useRef(null);

  // Body overflow locking and ESC handler when fullscreen
  useEffect(() => {
    if (isFs) {
      document.body.style.overflow = 'hidden';
      const handleEsc = (e) => {
        if (e.key === 'Escape') setFs(false);
      };
      window.addEventListener('keydown', handleEsc);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleEsc);
      };
    } else {
      document.body.style.overflow = '';
    }
  }, [isFs, setFs]);

  // Update initial welcome message and profile when lang switches
  useEffect(() => {
    setUserProfile(prev => ({ ...prev, language: lang }));
    setMessages([
      {
        id: Date.now(),
        sender: 'bot',
        text: t.simWelcomeText || '',
        buttons: t.simWelcomeButtons || [],
        time: '10:00 AM'
      }
    ]);
    if (streamRef.current) {
      streamRef.current.scrollTop = 0;
    }
  }, [lang, t]);

  // Handle smart scrolling: stay at top for welcome message, scroll down for active chat
  useEffect(() => {
    if (streamRef.current) {
      if (messages.length > 1 || isTyping) {
        streamRef.current.scrollTop = streamRef.current.scrollHeight;
      } else {
        streamRef.current.scrollTop = 0;
      }
    }
  }, [messages, isTyping]);

  const getCurrentTime = () => {
    const d = new Date();
    let hours = d.getHours();
    let minutes = d.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    minutes = minutes < 10 ? '0' + minutes : minutes;
    return `${hours}:${minutes} ${ampm}`;
  };

  const handleSendMessage = async (customText, customAction) => {
    if (isTyping) return;

    const textToSend = customText !== undefined ? customText : inputVal;
    if (!textToSend && !customAction) return;

    if (textToSend && messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.sender === 'user' && lastMsg.text === textToSend) {
        return;
      }
    }

    if (textToSend) {
      setMessages(prev => [
        ...prev,
        {
          id: Date.now(),
          sender: 'user',
          text: textToSend,
          time: getCurrentTime()
        }
      ]);
      setInputVal('');
    }

    setIsTyping(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: textToSend,
          action: customAction,
          language: lang,
          userProfile: {
            ...userProfile,
            language: lang
          }
        })
      });

      const data = await response.json();
      setIsTyping(false);

      if (data.profile) {
        setUserProfile(data.profile);
      }

      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          sender: 'bot',
          text: data.text,
          buttons: data.buttons || [],
          time: getCurrentTime(),
          recipe: data.recipe
        }
      ]);
    } catch (err) {
      console.error('Chat API error:', err);
      setIsTyping(false);
      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          sender: 'bot',
          text: lang === 'English'
            ? `⚠️ <i>Server connection delay. Please try again in a moment.</i>`
            : (lang === 'Tamil'
                ? `⚠️ <i>சர்வர் இணைப்பில் தாமதம். தயவுசெய்து சிறிது நேரம் கழித்து மீண்டும் முயற்சிக்கவும்.</i>`
                : (lang === 'Hindi'
                    ? `⚠️ <i>सर्वर कनेक्शन में देरी। कृपया कुछ समय बाद पुनः प्रयास करें।</i>`
                    : (lang === 'Telugu'
                        ? `⚠️ <i>సర్వర్ కనెక్షన్ ఆలస్యం. దయచేసి కాసేపటి తర్వాత మళ్లీ ప్రయత్నించండి.</i>`
                        : `⚠️ <i>تأخر في الاتصال بالخادم. يرجى المحاولة مرة أخرى بعد قليل.</i>`))),
          time: getCurrentTime()
        }
      ]);
    }
  };

  const handleButtonClick = (btn, msgId) => {
    if (isTyping) return;

    const actionKey = `${msgId}_${btn.action}`;
    if (clickedActions.has(actionKey)) return;
    setClickedActions(prev => new Set(prev).add(actionKey));

    if (btn.action && btn.action.startsWith('open_modal_')) {
      const parts = btn.action.replace('open_modal_', '').split('_');
      const id = parseInt(parts[0], 10);
      const hc = parseInt(parts[1] || '4', 10);
      onOpenRecipeModal({ id }, hc);
      return;
    }
    handleSendMessage('', btn.action);
  };

  const getCookTodayQuery = () => {
    if (lang === 'English') return 'cook today';
    if (lang === 'Tamil') return 'இன்று என்ன சமைப்பது';
    if (lang === 'Hindi') return 'आज क्या पकाएं';
    if (lang === 'Telugu') return 'ఈరోజు ఏమి వండాలి';
    if (lang === 'Arabic') return 'ماذا أطبخ اليوم';
    return 'cook today';
  };

  return (
    <section id="aiAssistantSection" className="section">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">{t.navSimulator}</span>
          <h2 className="section-title">{t.simTitle}</h2>
          <p className="section-desc">{t.simSubtitle}</p>
        </div>

        <div className="simulator-container">
          <div className={`tg-app-window ${isFs ? 'fullscreen' : ''}`}>
            <div className="tg-window-header">
              <div className="tg-bot-identity">
                <div className="tg-bot-avatar-main">👩‍🍳</div>
                <div className="tg-bot-info">
                  <h4>{t.simBotName}</h4>
                  <div className="tg-bot-status">{t.simBotStatus}</div>
                </div>
              </div>

              <div className="tg-header-actions">
                <span className="tg-lang-indicator">
                  {lang === 'Tamil' ? '🇮🇳 தமிழ்' : (lang === 'Hindi' ? '🇮🇳 हिंदी' : (lang === 'Telugu' ? '🇮🇳 తెలుగు' : (lang === 'Arabic' ? '🇸🇦 العربية' : '🇬🇧 EN')))}
                </span>
                <button
                  type="button"
                  className={`tg-fs-btn ${isFs ? 'active' : ''}`}
                  onClick={() => setFs(!isFs)}
                  title={isFs ? t.btnExitFullscreen : t.btnFullscreen}
                  aria-label="Toggle Fullscreen"
                >
                  <span className="fs-icon">{isFs ? '🗗' : '⛶'}</span>
                  <span className="fs-text">{isFs ? t.btnExitFullscreen : t.btnFullscreen}</span>
                </button>
                {isFs && (
                  <button
                    type="button"
                    className="tg-fs-close-btn"
                    onClick={() => setFs(false)}
                    title={t.btnExitFullscreen}
                    aria-label="Close Fullscreen"
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>

            <div className="tg-messages-stream" ref={streamRef}>
              {messages.map(m => (
                <div key={m.id} className={`tg-msg tg-msg-${m.sender}`}>
                  {m.sender === 'bot' && <div className="tg-bot-avatar">👩‍🍳</div>}
                  <div className={m.sender === 'bot' ? 'tg-bot-body' : ''}>
                    <div className="tg-msg-bubble">
                      <div
                        className="tg-msg-text"
                        dangerouslySetInnerHTML={{ __html: (m.text || '').replace(/\n/g, '<br>') }}
                      />
                      <div className="tg-msg-meta">
                        <span>{m.time}</span>
                        {m.sender === 'user' && <span className="tg-check">✓✓</span>}
                      </div>
                    </div>

                    {m.buttons && m.buttons.length > 0 && (
                      <div className="tg-keyboard">
                        {m.buttons.map((row, rIdx) => (
                          <div key={rIdx} className="tg-keyboard-row">
                            {row.map((btn, bIdx) => {
                              const actionKey = `${m.id}_${btn.action}`;
                              const isClicked = clickedActions.has(actionKey);

                              return btn.url ? (
                                <a
                                  key={bIdx}
                                  href={btn.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="tg-btn"
                                >
                                  {btn.text}
                                </a>
                              ) : (
                                <button
                                  key={bIdx}
                                  type="button"
                                  className={`tg-btn ${isClicked ? 'pressed' : ''}`}
                                  disabled={isTyping}
                                  onClick={() => handleButtonClick(btn, m.id)}
                                >
                                  {btn.text}
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="tg-typing" style={{ display: 'flex' }}>
                  <span></span><span></span><span></span>
                </div>
              )}
            </div>

            <div className="tg-quick-bar">
              <button
                type="button"
                className="tg-quick-chip"
                disabled={isTyping}
                onClick={() => handleSendMessage(getCookTodayQuery())}
              >
                {t.simChipCook}
              </button>
              <button
                type="button"
                className="tg-quick-chip"
                disabled={isTyping}
                onClick={() => handleSendMessage('/find biryani')}
              >
                {t.simChipBiryani}
              </button>
              <button
                type="button"
                className="tg-quick-chip"
                disabled={isTyping}
                onClick={() => handleSendMessage('/find dosa')}
              >
                {t.simChipDosa}
              </button>
              <button
                type="button"
                className="tg-quick-chip"
                disabled={isTyping}
                onClick={() => handleSendMessage('/ingredients')}
              >
                {t.simChipPantry}
              </button>
              <button
                type="button"
                className="tg-quick-chip"
                disabled={isTyping}
                onClick={() => handleSendMessage('/plan')}
              >
                {t.simChipPlan}
              </button>
              <button
                type="button"
                className="tg-quick-chip"
                disabled={isTyping}
                onClick={() => handleSendMessage('/start')}
              >
                {t.simChipSettings}
              </button>
            </div>

            <div className="tg-input-bar">
              <input
                type="text"
                className="tg-input-field"
                placeholder={t.simInputPlaceholder}
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <button
                type="button"
                className="tg-send-btn"
                disabled={isTyping}
                onClick={() => handleSendMessage()}
                title={t.simSendTooltip}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="simulator-sidebar">
            <div className="sim-guide-block">
              <h4>{t.simSidebarTitle}</h4>
              <ul className="sim-guide-list">
                <li>{t.simSidebarFeature1}</li>
                <li>{t.simSidebarFeature2}</li>
                <li>{t.simSidebarFeature3}</li>
                <li>{t.simSidebarFeature4}</li>
              </ul>
            </div>

            <div className="sim-bot-qr-box">
              <h4>{t.simSidebarCardTitle}</h4>
              <p>{t.simSidebarCardDesc}</p>
              <a href="#recipesSection" className="btn btn-primary btn-sm" style={{ width: '100%' }}>
                {t.simSidebarCardBtn}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
